import { useEffect, useRef } from 'react';
import { fallbackLocale } from 'shared/assets/localization';
import { useToday } from 'shared/contexts/today';
import { useCreatePushNotificationMutation } from 'shared/hooks/queries/use-create-push-notification-mutation';
import { usePushNotificationMessageQuery } from 'shared/hooks/queries/use-push-notification-message-query';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useUser } from 'shared/hooks/use-user';
import { getPushNotificationTokenDate } from 'shared/services/local-storage';
import { PushNotificationMessageRequestState } from 'shared/services/local-storage/push-notification-message';

export const useDailyPushNotificationCheck = () => {
  const today = useToday();
  const { mutate: requestPermission } = useCreatePushNotificationMutation();
  const user = useUser();
  const { data: messageState } = usePushNotificationMessageQuery(user?.uid);
  const [permission] = useNotificationPermission();
  const hasRequested = useRef(false);

  useEffect(() => {
    if (
      !user?.uid ||
      messageState !== PushNotificationMessageRequestState.Asked ||
      permission !== 'granted'
    ) {
      return;
    }

    const lastTokenDate = getPushNotificationTokenDate(user.uid);
    const daysSinceLastToken = lastTokenDate
      ? Math.floor(
          (today.getTime() - lastTokenDate.getTime()) / (1000 * 60 * 60 * 24),
        )
      : 270;

    if (daysSinceLastToken > 200) {
      if (hasRequested.current) {
        return;
      }
      hasRequested.current = true;

      requestPermission({
        userId: user.uid,
        locale: user.settings?.language ?? fallbackLocale,
      });
    }
  }, [
    user?.uid,
    user?.settings?.language,
    requestPermission,
    messageState,
    permission,
    today,
  ]);
};
